import TYPES             from "@/core/services/Changelogs/types";

import Decimal           from "decimal.js";
import Title_ru_RU       from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";

import Core              from '@/core/Core';
import AbstractDemand    from "@/domains/Abstract/AbstractDemand";
import DemandOption      from '@/domains/DemandsV2/DemandOption';
import DemandRequest     from '@/domains/DemandsV2/DemandRequest';
import DateSimple        from "@/core/types/DateSimple";
import Price             from "@/core/types/Price";
import Unit              from "@/domains/Units/Unit";
import Product           from "@/domains/Products/Product";
import OrderPosition     from "@/domains/Orders/OrderPosition";
import Sum               from "@/core/types/Sum";
import Qty from "@/core/types/Qty";
import VatRate           from "@/core/types/VatRate";

/** Причины по которым нельзя сделать то или иное действие */
const REASON_UNAVAILABLE = {
  NO_CHOSEN_OPTION:           			'Не выбран вариант для удовлетворения потребности',
  NO_EXIST_ENTITY_ON_BACKEND: 			'Потребность ещё не создана',
  OPTIONS_SENT_TO_CHOOSE:           'Варианты уже отправлены на выбор',
  NOT_SENT_TO_APPROVE:              'Потребность не отправлена на проверку',
  IS_APPROVED:                			'Потребность согласована',
  IS_NOT_APPROVED:            			'Потребность не согласована',
  IS_NOT_ALLOWED:             			'Действие недоступно',
  SELLER_STORE_NO_EXIST:      			'В выбранной опции не указан склад поставщика',
  QTY_SELECTED_EXCEEDS_QTY_DESIRED: 'Количество в вариантах превышает количество в потребности',
  ORDERS_CREATING:                  'Заказы по потребности ещё создаются',
} as const;

const PRICE_DIFFERENCE_EDGE_PERCENT = 10;

@Title_ru_RU_Class('Потребность')
class DemandV2 extends AbstractDemand {
  static Events = {
    Created: 'DemandV2:created',
    Updated: 'DemandV2:updated',
    Deleted: 'DemandV2:deleted',

    Approved: 'DemandV2:approved',
    Rejected: 'DemandV2:rejected',
  }

  /**
   * Стадии жизненного цикла потребности — внутреннее состояние фронта,
   * наружу не передаются. Считаются по флагам isOptionsExists /
   * isOptionsSelected / isApproved и заказанному количеству.
   * Finished — как колонка is_finished в БД (qtyOrdered >= qtyDesired)
   */
  static readonly Stages = {
    New:              'new',               // поиск/создание вариантов
    AwaitingReview:   'awaiting-review',   // выбор вариантов
    AwaitingApproval: 'awaiting-approval', // ждёт одобрения (контроль бюджета)
    AwaitingOrder:    'awaiting-order',    // одобрено, ждёт создания заказа
    Finished:         'finished',          // заказ оформлен
    Deleted:          'deleted',
  } as const;

  /** --------- маркеры для реестра, стадии жизни заявки ------------ */

  /** Валюта в который мы хотим отображать предложения в карточке */
  currencyCode: string;

  /** в идеале это должно быть "если ли варианты у потребности", но увы,
   * получается все новые заявки получают isOptionsExists = false, когда мы добавляем варианты
   * и отправляем дальше по реестру, то становится true
   *
   * есть вариант когда мы можем вернуть потребность со стадии isApproved в самое начало,
   * но мы не удаляем же опции, но маркировать начала реестра нужно, вот у нас есть isOptionsExists
   * для этого
   * */
  @Title_ru_RU('вариант(ы) готов(ы) к выбору', TYPES.BOOLEAN_MARK)
  isOptionsExists: boolean;

  /** Есть ли выбранные варианты у потребности */
  @Title_ru_RU('вариант(ы) готов(ы) для утверждения', TYPES.BOOLEAN_MARK)
  isOptionsSelected: boolean;

  /** Согласована ли потребность или нет */
  @Title_ru_RU('утверждено', TYPES.BOOLEAN_MARK)
  isApproved: boolean;

  @Title_ru_RU('заказ оформлен', TYPES.BOOLEAN_MARK)
  isFinished: boolean;

  /** Заявки */
  requests: DemandRequest[];
  /** Наименование */
  private _name: string;

  /** Варианты, предложенные поставщиков в заявке */
  options: DemandOption[];

  /** Артикул */
  buyersCode: string;

  /** Номер заявки */
  @Title_ru_RU('номер заявки', TYPES.STRING)
  requestNumber: string;

  /** Категория: полиграфия или сырьё */
  @Title_ru_RU('категория заказа', TYPES.OBJECT)
  orderCategory: { id: string; name: string };

  /** Ближайшая дата доставки */
  @Title_ru_RU('ближайшая дата доставки', TYPES.DATE)
  minDesiredReceiptDate: DateSimple;

  /** --------- прочее ------------ */

  /** Макет */
  layout: any;

  /** Приоритет, цветовая индикация */
  priorityType: any;

  prevDeals: OrderPosition[];

  sumFullSeller: Sum | null;

  maxPrice: Price | null;

  prevPrice: Price | null;

  /** нужен для disable поля пока ждем ответа из сервиса заказов */
  isCreatingOrders: boolean = false;

  /** ================ Статические конструкторы ================ */

  static fromServer(demandV2Data: any, result = new DemandV2()): DemandV2 {
    super.fromServer(demandV2Data, result);

    result.orderCategory = Core.dictionaries.orderCategories.get(demandV2Data.orderCategory.id)
    result.minDesiredReceiptDate = DateSimple.fromServer(demandV2Data.minDesiredReceiptDate);

    result.prevDeals = Core.utils.hydrateArray(demandV2Data.prevDeals, OrderPosition.fromServer);

    /**
     * Почему-то из запроса в запрос после обновления опции мне постоянно приходит массив с разным
     * порядком сущностей, из-за этого таблица прыгает, я пишу этот временный фикс
     */
    result.options = Core.utils.hydrateArray(demandV2Data.options, DemandOption.fromServer)
      .sort((a: DemandOption, b: DemandOption) => a.id - b.id);
    // DemandV2 — хозяин связи DemandV2↔DemandOption, поэтому привязка здесь, а не в опции.
    result.options.forEach(_option => {
      _option.demand = result;

      /** Варианты, сохранённые до того, как бэк начал принимать условия НДС
       *  покупателя, приезжают без них. Ставка выводится из категории товара,
       *  одной на всю потребность, — берём оттуда, и при первом сохранении
       *  вариант дозаполнится сам. */
      if (_option.vatRateBuyer === null || _option.vatRateBuyer.isEmpty()) {
        _option.vatRateBuyer = result.vatRateBuyer;
        _option.isVatIncludedBuyer = result.isVatIncludedBuyer;
      }
    })


    /** Стрелка, а не переданный напрямую метод: hydrateArray отдаёт в колбэк
     *  ещё и индекс, а вторым параметром fromServer идёт result. */
    result.requests = Core.utils.hydrateArray(demandV2Data.requests, (requestData) => DemandRequest.fromServer(requestData));
    // DemandV2 — хозяин связи DemandV2↔DemandRequest, поэтому привязка здесь, а не в заявке.
    result.requests.forEach(_request => { _request.demand = result })

    result.sumFullSeller = Core.utils.hydrate(demandV2Data.sumFullSeller, Sum.fromServer);
    result.maxPrice = Core.utils.hydrate(demandV2Data.maxPrice, Price.fromServer);
    result.prevPrice = Core.utils.hydrate(demandV2Data.prevPrice, Price.fromServer);

    return result;
  }

  static fromNew(result = new DemandV2()): DemandV2 {
    super.fromNew(result);

    result.orderCategory = Core.dictionaries.orderCategories.get('rawMaterialsAndPackage')

    result.options = [];

    result.requests = [new DemandRequest()];
    result.requests.forEach(_request => { _request.demand = result })

    result.unit = Unit.initDefault();

    /** Ставка НДС покупателя заполняется сеттером productCategory, а
     *  категории у новой потребности ещё нет. Без ставки не рассчитать цену
     *  покупателя в вариантах, поэтому ставим ставку локальной юрисдикции:
     *  конфиг заменяется при деплое, RU и Namangan получают свои значения. */
    result.vatRateBuyer = VatRate.fromServer(Core.config.localDefaultVatRate);

    return result;
  }

  /**
   * Когда мы хотим обогатить имеющуюся потребность, на самом деле
   * мы хотим прикрепить создаваемую потребность к уже имеющейся,
   * прикрепить, потому что ЖДД будет другая
   */
  static enrich(demandV2Data) {
    const newDemand    = DemandV2.fromServer(demandV2Data);

    newDemand.requests = [new DemandRequest()];
    newDemand.requests.forEach(_request => { _request.demand = newDemand })

    return newDemand;
  }

  /**
   * В сюжете, когда пользователь случайно кликнул во время создания на
   * "Обогатить имеющуюся потребность", и пытается вернуться к состоянию до,
   * то отрабатывает этот метод
   */
  static prune(demandV2: DemandV2): DemandV2 {
    const pureDemandV2 = new DemandV2();
    pureDemandV2.product = demandV2.product;
    return pureDemandV2;
  }

  /** ================ Геттеры/сеттеры ================ */

  /**
   * Строковое представление: тип позиции (из @Title_ru_RU_Class) + название товара, если задано
   */
  toString(): string {
    const type = (this.constructor as any).entityTypeName;

    if (this.name) {
      return `${type}: ${this.name}`;
    }
    else {
      return type;
    }
  }

  set product(value: Product | undefined) {
    super.product = value;

    if (!value) {
      this._name = undefined;
      this.buyersCode = undefined;
      this.unit = Unit.initDefault() as Unit;
      return;
    }

    this._name = value.name;

    if (value.id) {
      this.buyersCode = value.code;
    }
  }

  get product(): Product {
    return super.product;
  }

  /** Одобренные варианты */
  get chosenOptions() {
    return this.options.filter(_option => _option.hasQtySelected())
  }

  get name() { return this._name ?? 'Создание потребности' }
  set name(value) { this._name = value }

  get hasOptions() { return Core.utils.isNotEmptyArray(this.options) }
  get hasChosenOptions() { return Core.utils.isNotEmptyArray(this.chosenOptions) }


  /**
   * Группирует использованные опции по поставщику
   */
  aggregateUsedOptionsBySeller(usedOptions: any[]): any[][] {
    const grouped: Record<number, any[]> = {};

    usedOptions.forEach(usedOption => {
      const sellerId = usedOption.option.seller.id;

      if (!grouped[sellerId]) {
        grouped[sellerId] = [];
      }

      grouped[sellerId].push(usedOption);
    });

    return Object.values(grouped);
  }

  /** ================ Стадии жизненного цикла: предикаты ================ */

  getStage(): string | null {
    if (this.isDeleted) {
      return DemandV2.Stages.Deleted;
    }
    else if (!this.isApproved && !this.isOptionsExists && !this.isOptionsSelected) {
      return DemandV2.Stages.New;
    }
    else if (!this.isApproved && this.isOptionsExists && !this.isOptionsSelected) {
      return DemandV2.Stages.AwaitingReview;
    }
    else if (!this.isApproved && this.isOptionsExists && this.isOptionsSelected) {
      return DemandV2.Stages.AwaitingApproval;
    }
    /** Заказано меньше желаемого — потребность в работе */
    else if (this.isApproved && this.qtyOrdered.lessThan(this.qtyDesired)) {
      return DemandV2.Stages.AwaitingOrder;
    }
    /** Заказано больше или равно желаемому — как колонка is_finished в БД (qty_ordered >= qty_desired) */
    else if (this.isApproved && this.qtyOrdered.greaterThanOrEqualTo(this.qtyDesired)) {
      return DemandV2.Stages.Finished;
    }
    else {
      return null;
    }
  }

  /** Стадия подбора вариантов (начальная) */
  get isWaitingForOptions(): boolean {
    return this.getStage() === DemandV2.Stages.New;
  }

  /** Стадия выбора из предложенных вариантов */
  get isWaitingForChoose(): boolean {
    return this.getStage() === DemandV2.Stages.AwaitingReview;
  }

  /** ================ Стадии жизненного цикла потребности в2 ================ */

  /** Отправить к подбору вариантов */
  moveToEnrichOptionsStage(): void {
    this.isOptionsExists   = false;
    this.isOptionsSelected = false;
    this.isApproved        = false;
  }

  /** Отправить к выбору вариантов */
  moveToChooseOptionsStage(): void {
    this.isOptionsExists   = true;
    this.isOptionsSelected = false;
    this.isApproved        = false;
  }

  /** Отправить на утверждение к фин. директору */
  moveToApproveOptionsStage(): void {
    this.isOptionsExists   = true;
    this.isOptionsSelected = true;
    this.isApproved        = false;
  }

  /** Фин. директор утверждает */
  approve(): void {
    this.isOptionsExists   = true;
    this.isOptionsSelected = true;
    this.isApproved        = true;
  }

  /** Фин. директор отклоняет */
  reject(): void {
    this.isApproved = false;
  }


  /** ================ Работа с опциями ================ */

  /**
   * previewOrderCreation высчитывает распределение выбранных опций по заявкам до создания заказа
   *
   * У потребности может быть несколько заявок (requests) и выбранные опции (chosenOptions)
   * requests — каждая со своим желаемым кол-вом (qtyDesired).
   * chosenOptions — каждая со своим выбранным кол-вом от поставщика (qtySelected).
   *
   * Расхождение возникает, когда опций больше чем нужно (ignoredQty > 0)
   * или меньше чем нужно (totalQtyUsed < totalQtyDesired).
   */

  previewOrderCreation(): {
    ordersCount: number;
    totalQtyUsed: Qty;
    totalQtyDesired: Qty;
    ignoredQty: Qty;
    orders: Array<{ sellerName: string; qty: Qty }>;
    ignoredOptions: Array<{ sellerName: string; qty: Qty }>;
  } {

    /** Распределение считается один раз: геттер проходит по всем заявкам,
     *  и звать его в цикле значило бы пересчитывать всё на каждой итерации. */
    const distribution = this.distribution;

    let totalQtyUsed = Qty.zero();
    const orders: Array<{ sellerName: string; qty: Qty }> = [];

    /** Сколько всего разошлось из каждой опции по всем заявкам.
     *  Раньше это лежало в option.qtyUsed и требовало сбросов до и после;
     *  теперь считается здесь из готового распределения. */
    const usedByOption = new Map<DemandOption, Qty>();

    /**
     * Проходим по каждой заявке и берём набранные для неё опции.
     * Опции расходуются последовательно — остаток от предыдущей заявки переходит к следующей,
     * это уже учтено внутри distribution.
     *
     * Например: заявка 1 нужна 100 шт., опция A даёт 60, опция B даёт 40 — обе уходят в заявку 1.
     * заявка 2 нужна 30 шт. — опций уже нет, orders для неё не создастся.
     * */

    for (const usedOptions of distribution.values()) {
      /** Группируем по поставщику: если два варианта от одного поставщика покрыли одну заявку,
       * это будет один заказ поставщику (а не два) */

      const groupedBySeller = this.aggregateUsedOptionsBySeller(usedOptions);

      for (const sellerGroup of groupedBySeller) {
        const sellerName = sellerGroup[0].option.seller.name;
        const qty        = sellerGroup.reduce((acc: Qty, { usedQty }) => acc.plus(usedQty), Qty.zero());

        orders.push({ sellerName, qty });
        totalQtyUsed = totalQtyUsed.plus(qty);
      }

      usedOptions.forEach(({ option, usedQty }) => {
        const alreadyUsed = usedByOption.get(option) ?? Qty.zero();

        usedByOption.set(option, alreadyUsed.plus(usedQty));
      });
    }

    /**
     * Ignored — опции, у которых после распределения по всем заявкам остался неиспользованный остаток.
     * Это происходит, когда суммарный qtyDesired по заявкам меньше суммарного qtySelected по опциям.
     *
     * Например: опция на 100 шт., а все заявки вместе взяли только 70 шт., а осталось 30 шт.
     * Эти остатки не войдут ни в один заказ.
     * */

    const ignoredOptions = this.chosenOptions
      .filter(option => option.qtySelected.greaterThan(usedByOption.get(option) ?? Qty.zero()))
      .map(option => ({ sellerName: option.seller.name, qty: option.qtySelected.minus(usedByOption.get(option) ?? Qty.zero()) }));

    const ignoredQty = ignoredOptions.reduce((acc, option) => acc.plus(option.qty), Qty.zero());

    /** Заявка без введённого количества в сумму не входит: null — это
     *  «не введено», а не ноль. Так же считает calculateDesiredQty. */
    const totalQtyDesired = this.requests.reduce((acc: Qty, request) => {
      if (request.qtyDesired === null) {
        return acc;
      }
      else {
        return acc.plus(request.qtyDesired);
      }
    }, Qty.zero());

    return { ordersCount: orders.length, totalQtyUsed, totalQtyDesired, ignoredQty, orders, ignoredOptions };
  }

  /**
   * Распределение выбранных опций по заявкам.
   *
   * Заявки набирают количество из chosenOptions по порядку: остаток от
   * предыдущей заявки переходит к следующей. Поэтому распределение —
   * свойство потребности целиком, а не отдельной заявки: сколько досталось
   * заявке, зависит от того, сколько взяли заявки до неё. Заявка читает
   * свою долю отсюда (DemandRequest.usedOptions).
   *
   * Геттер, а не поле: на сервере распределения нет ни в каком виде, это
   * расчёт для интерфейса. Он чист — опции при нём не меняются, расход
   * держится в локальной Map одного прохода. Прежняя схема писала расход в
   * option.qtyUsed, из-за чего расчёт нельзя было звать повторно без
   * сброса, а геттером он не мог быть вовсе.
   */
  get distribution(): Map<DemandRequest, Array<{ option: DemandOption; usedQty: Qty }>> {
    const result = new Map<DemandRequest, Array<{ option: DemandOption; usedQty: Qty }>>();

    /** Расход опций на время этого прохода. Наружу не выходит: следующее
     *  чтение геттера начинает распределение с нуля. */
    const usedByOption = new Map<DemandOption, Qty>();

    this.requests.forEach((request) => {
      result.set(request, this.takeOptionsSatisfyingDesiredQty(request.qtyDesired, usedByOption));
    });

    return result;
  }

  /**
   * takeOptionsSatisfyingDesiredQty набирает опции для покрытия кол-ва заявки.
   *
   * проходит по chosenOptions по порядку и берёт из каждой столько, сколько нужно,
   * пока не наберёт qty или не закончатся опции.
   *
   * Уже израсходованное предыдущими заявками приходит и уходит через
   * usedByOption — она мутируется, это состояние одного прохода
   * распределения. Сами опции метод не трогает.
   */
  takeOptionsSatisfyingDesiredQty(
    qty:          Qty | null,
    usedByOption: Map<DemandOption, Qty>,
  ): Array<{ option: DemandOption; usedQty: Qty }> {
    const selectedOptions: Array<{ option: DemandOption; usedQty: Qty }> = [];

    if (qty === null) {
      // Количество не введено — набирать нечего
      return selectedOptions;
    }
    else {
      let totalSelectedQty = Qty.zero();

      for (let option of this.chosenOptions) {
        /** «Набрали нужное» — это >=, а у Numeric есть только строгие сравнения:
         *  не меньше желаемого = не lessThan. */
        if (totalSelectedQty.lessThan(qty) === false) break; // уже набрали нужное кол-во

        const alreadyUsed = usedByOption.get(option) ?? Qty.zero();

        /** availableToTake учитывает, что часть qtySelected могла быть уже израсходована предыдущими заявками */

        const availableToTake = option.qtySelected.minus(alreadyUsed);
        if (availableToTake.isZero()) continue; // опция полностью израсходована

        const remainingQty = qty.minus(totalSelectedQty);

        /** Math.min на Qty не работает — берём меньшее явным сравнением. */
        let qtyToTake: Qty;

        if (availableToTake.lessThan(remainingQty)) {
          qtyToTake = availableToTake;
        }
        else {
          qtyToTake = remainingQty;
        }

        usedByOption.set(option, alreadyUsed.plus(qtyToTake)); // фиксируем использование в рамках прохода

        selectedOptions.push({ option, usedQty: qtyToTake });
        totalSelectedQty = totalSelectedQty.plus(qtyToTake);
      }

      return selectedOptions;
    }
  }

  get isChosenMoreThen1Option() { return this.chosenOptions.length > 1 }

  /** ================ Работа с заявками ================ */

  calculateDesiredQty() {
    this.qtyDesired = this.requests.reduce((acc: Qty, item) => {
      if (item.qtyDesired === null) {
        return acc;
      }
      else {
        return acc.plus(item.qtyDesired);
      }
    }, Qty.zero());
  }

  calculateMinDesiredReceiptDate() {
    if (Core.utils.isEmptyArray(this.requests)) {
      /** Заявок нет — считать не из чего, прежнее значение (оно же
       *  серверное, из агрегата) остаётся. Прежний reduce без начального
       *  значения на пустом массиве бросал TypeError. */
    }
    else {
      let earliest: DateSimple | null = null;

      this.requests.forEach((request) => {
        if (request.desiredReceiptDate === null) {
          /** Дата не заполнена — в сравнении не участвует. Прежнее
           *  операторное сравнение приводило null к нулю, то есть к
           *  1970 году, и такая заявка всегда оказывалась самой ранней. */
        }
        else if (earliest === null) {
          earliest = request.desiredReceiptDate;
        }
        else if (request.desiredReceiptDate.isBefore(earliest)) {
          earliest = request.desiredReceiptDate;
        }
        else {
          // Заявка не раньше текущего минимума
        }
      });

      if (earliest === null) {
        // Ни одной заполненной даты — прежнее значение не трогаем
      }
      else {
        this.minDesiredReceiptDate = earliest;
      }
    }
  }

  /** ================ Сравнение цены с прошлым заказом ================ */

  get priceDifferencePercent(): Decimal | null {
    if (this.maxPrice === null || this.maxPrice === undefined) {
      return null;
    }
    else if (this.prevPrice === null || this.prevPrice === undefined) {
      return null;
    }
    else if (this.prevPrice.isZero()) {
      return null;
    }
    else {
      return this.maxPrice.divByPrice(this.prevPrice).asPercent();
    }
  }

  getPriceDifferenceColor(): string {
    const diff = this.priceDifferencePercent;

    if (diff === null) {
      return 'var(--faint-text-strong)';
    }
    else if (diff.isZero()) {
      return 'var(--faint-text-strong)';
    }
    else if (diff.isNegative()) {
      return 'green';
    }
    else if (diff.lessThan(PRICE_DIFFERENCE_EDGE_PERCENT)) {
      return 'orange';
    }
    else {
      return 'red';
    }
  }

  getPriceDifferenceTooltip(): string | null {
    let tooltip: string | null;

    if (this.maxPrice === null || this.maxPrice === undefined) {
      tooltip = null;
    }
    else if (this.prevPrice === null || this.prevPrice === undefined) {
      tooltip = 'Нет данных о предыдущей цене';
    }
    else {
      const diff = this.priceDifferencePercent;

      if (diff === null) {
        tooltip = null;
      }
      else if (diff.isZero()) {
        tooltip = null;
      }
      else if (diff.isNegative()) {
        tooltip = 'Цена выгоднее чем в прошлый раз';
      }
      else if (diff.lessThan(PRICE_DIFFERENCE_EDGE_PERCENT)) {
        tooltip = `Расхождение цены с прошлым заказом менее ${PRICE_DIFFERENCE_EDGE_PERCENT}%`;
      }
      else {
        tooltip = `Расхождение цены с прошлым заказом более ${PRICE_DIFFERENCE_EDGE_PERCENT}%`;
      }
    }

    if (this.chosenOptions.length === 1) {
      const singleOptionText = 'У потребности выбран только один вариант';

      if (tooltip === null) {
        tooltip = singleOptionText;
      }
      else {
        tooltip = `${singleOptionText}\n\n${tooltip}`;
      }
    }

    return tooltip;
  }

  /** ================ Валидация действий ================ */

  /**
   * Проверяет можно ли согласовать потребность
   */
  getReasonCannotApprove(): string | null {
    if (!this.id) return REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    if (this.isApproved) return REASON_UNAVAILABLE.IS_APPROVED;
    if (this.hasChosenOptions === false) return REASON_UNAVAILABLE.NO_CHOSEN_OPTION;
    if (this.isOptionsSelected === false) return REASON_UNAVAILABLE.NOT_SENT_TO_APPROVE;
    return null;
  }

  /**
   * Проверяет можно ли добавить вариант
   */
  getReasonCannotAddOption(): string | null {
    if (this.isApproved === true) return REASON_UNAVAILABLE.IS_APPROVED;
    if (this.isOptionsExists === true) return REASON_UNAVAILABLE.OPTIONS_SENT_TO_CHOOSE;
    return null;
  }

  /**
   * Проверяет можно ли создать заказ из потребности
   */

  getReasonCannotCreateOrder(): string | null {
    if (!this.id) return REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    if (this.isCreatingOrders === true) return REASON_UNAVAILABLE.ORDERS_CREATING;
    if (!this.isApproved) return REASON_UNAVAILABLE.IS_NOT_APPROVED;

    const selectedOptions = this.options.filter(option => option.qtySelected.isPositive());
    const optionsWithoutSellerStore = selectedOptions.filter(option => !option.sellerStore?.id);

    if (optionsWithoutSellerStore.length > 0) return REASON_UNAVAILABLE.SELLER_STORE_NO_EXIST;

    return null;
  }

  /**
   * Проверяет можно ли отправить потребность на доработку
   */
  getReasonCannotMove(): string | null {
    if (!this.id) return REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    if (!this.isOptionsExists && !this.isOptionsSelected && !this.isApproved) {
      return REASON_UNAVAILABLE.IS_NOT_ALLOWED;
    }
    return null;
  }
}

export default DemandV2;
